import React, { useState, useEffect } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { updateMonitoringInfo } from "../UpdateMonitoringInfo";
import { months } from "../../../../util/CommonLists";
import { PATTERNS } from "../../../../util/patterns";
import { emailMasking } from "../../../../util/CommonReadonlyMasking";
import { useDispatch, useSelector } from "react-redux";
import { getFlowByName } from "../../../../config/FlowName";
import * as NextStepFlowAction from "../../../../store/nextStepFlowService/actions";
import { ApplicationState } from "../../../../store";
import i18nService from "../../../../util/Internationalization/i18n/i18n";
import { Notification } from "../../../common/Notification";
import PhoneInput, { parsePhoneNumber } from "react-phone-number-input";
import { useTranslation } from "react-i18next";
import { isValidPhoneNo } from "../../../../util/phoneNumberValidationUtil";

interface IProps {
    refreshDataCallback(): void;
    setStatus: any;
    status: boolean;
    formData: any;
    setFormData: any;
    addressInfo: any;
    activeStatus: any;
}

const PersonalInfoForm = ({ refreshDataCallback, addressInfo, setStatus, status, formData, setFormData, activeStatus }: IProps) => {
    const isIDFUser = useSelector((state: ApplicationState) =>
        state.myAccountServiceState && state.myAccountServiceState.isIdfUser ? state.myAccountServiceState.isIdfUser : false
    );
    const [loading, setLoading] = useState(false);
    const [visibleEmail, setVisisbleEmail]: any = useState([]);
    const dispatch = useDispatch();
    const monitoringInfoFlow = useSelector((state: ApplicationState) => getFlowByName(state, "ADD-YOUR-ADDITIONAL-INFORMATION"));
    const completeGetStartedItem = (flowID: any, flowName: string) => dispatch(NextStepFlowAction.actionCreators.completeGetStartedItem(flowID, flowName));
    const isPrimaryAccount = useSelector((state: ApplicationState) =>
        state.loggedInUser && state.loggedInUser.activeAccount && state.loggedInUser.activeAccount.accountType
            ? state.loggedInUser.activeAccount.accountType.toLowerCase() === "primary"
            : false
    );
    const isLife360PartnerId = useSelector((state: ApplicationState) =>
        state.configurationState && state.configurationState.configuration.partnerConfig.partnerId
            ? state.configurationState.configuration.partnerConfig.partnerId === 539
                ? true
                : false
            : false
    );
    const accountInfo = useSelector((state: ApplicationState) => (state.myAccountServiceState ? state.myAccountServiceState._unifiedAccountInfo : null));
    const { t: tLang, i18n: i18nLang } = useTranslation();

    /**Validation with react-hook-form ***/
    const { register, handleSubmit, getValues, errors, control, setError, clearErrors, trigger, watch } = useForm({
        mode: "onBlur",
        defaultValues: {
            birthDay: formData["date-of-birth"] ? formData["date-of-birth"].split("/")[1] : "",
            birthMonth: formData["date-of-birth"] ? months[Number(formData["date-of-birth"].split("/")[0]) - 1] : "",
            birthYear: formData["date-of-birth"] ? formData["date-of-birth"].split("/")[2] : "",
            emails: formData.emails ? formData.emails : [{ email: "" }],
            firstName: formData.firstName,
            lastName: formData.lastName,
            phone: formData.phone ? formData.phone : [{ phone: "" }],
            securityNumber: formData.securityNumber ? `XXX-XX-${formData.securityNumber.substr(formData.securityNumber.length - 4)}` : "",
            internationalSecurityNumber: formData.internationalSecurityNumber["sin"]
                ? `XXX-XXX-${formData.internationalSecurityNumber["sin"].substr(formData.internationalSecurityNumber["sin"].length - 3)}`
                : "",
            nationalIdentificationNumber: formData.nationalIdentificationNumber["nid"] ? `XXX XXX ${formData.nationalIdentificationNumber["nid"].substr(6)}` : "",
            "check-ssn-3-digit": "",
            "check-ssn-2-digit": "",
            "check-ssn-4-digit": "",
        },
    });

    const emailHandler = useFieldArray({
        control,
        name: "emails",
    });

    useEffect(() => {
        disableFlag();
    }, []);

    const disableFlag = () => {
        setTimeout(() => {
            const elements = document.getElementsByClassName("PhoneInputCountry");
            if (elements && elements.length > 0) {
                try {
                    for (const el of elements) {
                        const childs = el.children;
                        if (childs[0].nodeName === "SELECT") {
                            const selectEl = childs[0] as HTMLSelectElement;
                            selectEl.disabled = true;
                        }
                    }
                } catch {}
            }
            const selectElement = document.getElementsByClassName("PhoneInputCountrySelect");
            if (selectElement && selectElement.length > 0) {
                for (const el of selectElement) {
                    if (el.tagName.toLowerCase() === "select") {
                        el.setAttribute("aria-required", "true");
                    }
                }
            }
        }, 500);
    };

    const toggleEmailVisibility = (item: any) => {
        if (visibleEmail.length > 0) {
            const index = visibleEmail.findIndex((element: any) => element.email === item.email);
            if (index >= 0) {
                visibleEmail.splice(index, 1);
                setVisisbleEmail([...visibleEmail]);
            } else {
                setVisisbleEmail([...visibleEmail, item]);
            }
        } else {
            setVisisbleEmail([...visibleEmail, item]);
        }
    };

    const phoneHandler = useFieldArray({
        control,
        name: "phone",
    });

    const getCountryInfo = () => {
        let currentCountry = i18nService.get("Country");
        if (currentCountry.includes("US") || currentCountry.includes("CA")) {
            return false;
        } else {
            return true;
        }
    };

    const getDuplicateValidationError = () => {
        let currentValidationError = tLang("identityVault.monitoredInformation.personalInfoWidget.validationMessage.mobileRequiredError"); 
        if (currentValidationError !== null) {
            return currentValidationError;
        } 
    };

    /***Handle submit action ***/
    const onSubmit = (data: any) => {
        setLoading(true);
        const ssnNumberr = data["check-ssn-3-digit"] + data["check-ssn-2-digit"] + data["check-ssn-4-digit"];

        data["date-of-birth"] = `${months.indexOf(data.birthMonth)}/${data.birthDay}/${data.birthYear}`;
        if (!isValidDateOfBirth(data)) {
            setLoading(false);
        } else if (underAgeValidate(data)) {
            setLoading(false);
            setError("birthYear", {
                type: "validate",
            });
        } else {
            addressInfo.country = addressInfo["address-country"];
            updateMonitoringInfo(data, addressInfo, ssnNumberr, formData, isPrimaryAccount, isIDFUser)
                .then((data: any) => {
                    if (monitoringInfoFlow && monitoringInfoFlow.flowStepStatus !== "COMPLETED") {
                        completeGetStartedItem(monitoringInfoFlow.flowStepId, monitoringInfoFlow.flowName ? monitoringInfoFlow.flowName : " ");
                    }
                    refreshDataCallback();
                    setLoading(false);
                    setStatus(false);
                })
                .catch(function (error) {
                    setLoading(false);
                    Notification({
                        type: "error",
                        title: "Error",
                        message: error,
                        duration: 0,
                    });
                });
        }
    };

    const underAgeValidate = (data: any) => {
        var currentDate = new Date();
        var dob = new Date(`${data.birthDay}/${data.birthMonth}/${data.birthYear}`);
        var myAge = currentDate.getFullYear() - dob.getFullYear();
        var date = currentDate.getMonth() - dob.getMonth();
        if (date < 0 || (date === 0 && currentDate.getDate() < dob.getDate())) {
            myAge--;
        }
        if (myAge < 18) {
            return true;
        } else {
            return false;
        }
    };

    const isValidDateOfBirth = (data: any) => {
        let isValid = true;
        if (data) {
            if (data && data.birthDay != null && data.birthDay === "") {
                setError("birthDay", {
                    type: "required",
                });
                isValid = false;
            }

            if (data && data.birthMonth != null && data.birthMonth === "") {
                setError("birthMonth", {
                    type: "required",
                });
                isValid = false;
            }

            if (data && data.birthYear != null && data.birthYear === "") {
                setError("birthYear", {
                    type: "required",
                });
                isValid = false;
            }
        } else {
            isValid = false;
        }

        return isValid;
    };

    function onTextBoxChangeHandler(event: React.ChangeEvent<HTMLInputElement>) {
        let targetName = event.target.name;
        let targetValue = event.target.value;

        switch (targetName) {
            case "check-ssn-3-digit":
                if (targetValue.length > 2) {
                    document.getElementById("check-ssn-2-digit")?.focus();
                }
                break;
            case "check-ssn-2-digit":
                if (targetValue.length > 1) {
                    document.getElementById("check-ssn-4-digit")?.focus();
                }
                break;
            default:
                break;
        }
    }

    const dateFields = [
        <div className="col-xs-4 col-no-gutter">
            <div className={`form-row row ${errors["birthDay"] && errors["birthDay"].type === "required" && "status-error"}`}>
                <div className="form-label sr-only col-xs-12 col-no-gutter">
                    <label htmlFor="label-dob-day">Date of Birth Day</label>
                </div>
                <div role="alert" className="form-input col-xs-12 col-no-gutter padding-right-xs">
                    <select
                        aria-required="true"
                        className="inputbox"
                        id="label-dob-day"
                        disabled={formData["date-of-birth"] && !!formData["date-of-birth"].split("/")[1]}
                        ref={register}
                        name="birthDay"
                        onChange={() => clearErrors("birthDay")}
                    >
                        <option value="">Day</option>
                        {formData["date-of-birth"] && formData["date-of-birth"].split("/")[1] ? (
                            <option value={formData["date-of-birth"] && formData["date-of-birth"].split("/")[1]}>{formData["date-of-birth"].split("/")[1]}</option>
                        ) : (
                            Array(31)
                                .fill(null)
                                .map((value, index) => {
                                    return (
                                        <option key={index + 1} value={index + 1}>
                                            {index + 1}
                                        </option>
                                    );
                                })
                        )}
                    </select>
                </div>
            </div>
        </div>,
        <div className="col-xs-4 col-no-gutter">
            <div className={`form-row row ${errors["birthMonth"] && errors["birthMonth"].type === "required" && "status-error"}`}>
                <div className="form-label sr-only col-xs-12 col-no-gutter">
                    <label htmlFor="label-dob-month">Date of Birth Month</label>
                </div>
                <div role="alert" className="form-input col-xs-12 col-no-gutter padding-right-xs">
                    <select
                        aria-required="true"
                        name="birthMonth"
                        className="inputbox"
                        id="label-dob-month"
                        disabled={formData["date-of-birth"] && !!formData["date-of-birth"].split("/")[0]}
                        ref={register}
                        onChange={() => clearErrors("birthMonth")}
                    >
                        <option value="">Month</option>
                        {formData["date-of-birth"] ? (
                            <option value={formData["date-of-birth"] && months[Number(formData["date-of-birth"].split("/")[0]) - 1]}>
                                {months[Number(formData["date-of-birth"].split("/")[0]) - 1]}
                            </option>
                        ) : (
                            months.map((month) => {
                                return (
                                    <option key={month} value={month}>
                                        {month}
                                    </option>
                                );
                            })
                        )}
                    </select>
                </div>
            </div>
        </div>,
        <div className="col-xs-4 col-no-gutter">
            <div
                className={`form-row row ${
                    errors["birthYear"] && (errors["birthYear"].type === "validate" || errors["birthYear"].type === "required") && "status-error"
                }`}
            >
                <div className="form-label sr-only col-xs-12 col-no-gutter">
                    <label htmlFor="label-dob-year">Date of Birth Year</label>
                </div>
                <div role="alert" className="form-input col-xs-12 col-no-gutter padding-right-xs">
                    <select
                        aria-required="true"
                        name="birthYear"
                        className="inputbox"
                        id="label-dob-year"
                        disabled={formData["date-of-birth"] && !!formData["date-of-birth"].split("/")[2]}
                        ref={register}
                        onChange={() => clearErrors("birthYear")}
                    >
                        <option value="">Year</option>
                        {formData["date-of-birth"] && formData["date-of-birth"].split("/")[2] ? (
                            <option value={formData["date-of-birth"].split("/")[2]}>****</option>
                        ) : (
                            Array(115)
                                .fill(null)
                                .map((value, index) => {
                                    const optionValue = new Date().getFullYear() - index;
                                    return (
                                        <option key={optionValue} value={optionValue}>
                                            {optionValue}
                                        </option>
                                    );
                                })
                        )}
                    </select>
                </div>
            </div>
        </div>,
    ];
    return (
        <div id="monitored-info-personal-information-form" className={`${loading && "loading"}`}>
            <form onSubmit={handleSubmit(onSubmit)} id="monitored-info-personal-information-form-container">
                {!isLife360PartnerId && (
                    <div className="form-row inline no-margin row" aria-labelledby="label-full-name" role="group">
                        <div className="group-label col-xs-12 col-sm-4 col-no-gutter" id="label-full-name">
                            {tLang("identityVault.monitoredInformation.personalInfoWidget.label.fullName")}{" "}
                            <span className="required" title="Mandatory">
                                *
                            </span>
                        </div>
                        <div className="group-rows col-xs-12 col-sm-6 col-no-gutter">
                            <div className="row">
                                <div className={errors["firstName"] ? "form-row row status-error" : "form-row row"}>
                                    <div className="form-label sr-only col-xs-12 col-no-gutter">
                                        <label htmlFor="label-first-name">
                                            First Name{" "}
                                            <span className="required" title="Mandatory">
                                                *
                                            </span>
                                        </label>
                                    </div>
                                    <div role="alert" className="form-input col-xs-12 col-no-gutter">
                                        <input
                                            aria-required="true"
                                            readOnly={!!formData.firstName}
                                            type="text"
                                            name="firstName"
                                            className="inputbox"
                                            id="label-first-name"
                                            placeholder={tLang("identityVault.monitoredInformation.personalInfoWidget.placeholder.firstName")}
                                            ref={register({
                                                required: true,
                                                pattern: PATTERNS.FIRSTNAME.pattern,
                                            })}
                                        />
                                        {errors["firstName"] && (
                                            <span className="form-error">
                                                {tLang("identityVault.monitoredInformation.personalInfoWidget.validationMessage.firstNameValidationMessage")}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                
                            </div>

                            <div className={`form-row row ${errors["lastName"] && "status-error"}`}>
                                <div className="form-label sr-only col-xs-12 col-no-gutter">
                                    <label htmlFor="label-last-name">
                                        Last Name{" "}
                                        <span className="required" title="Mandatory">
                                            *
                                        </span>{" "}
                                    </label>
                                </div>
                                <div role="alert" className="form-input col-xs-12 col-no-gutter">
                                    <input
                                        aria-required="true"
                                        type="text"
                                        readOnly={!!formData.lastName}
                                        name="lastName"
                                        className="inputbox"
                                        id="label-last-name"
                                        placeholder={tLang("identityVault.monitoredInformation.personalInfoWidget.placeholder.lastName")}
                                        ref={register({ required: true })}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeStatus.isEmailAddressMonitoringActive &&
                    emailHandler.fields.map((item, index) => {
                        return (
                            <div className={`form-row inline row ${errors["emails"] && errors["emails"][index] && "status-error"}`} key={item.id}>
                                <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                                    {index === 0 ? (
                                        <label htmlFor={`label-email-${index + 1}`}>
                                            {tLang("identityVault.monitoredInformation.personalInfoWidget.label.email")}
                                            {isPrimaryAccount && (
                                                <span className="required" title="Mandatory">
                                                    {" "}
                                                    *
                                                </span>
                                            )}
                                        </label>
                                    ) : <label htmlFor={`label-email-${index + 1}`} className="hide-label" >
                                            {tLang("identityVault.monitoredInformation.personalInfoWidget.label.email")}{`${' '.repeat(index + 1)}`}
                                    </label>}
                                </div>
                                <div role="alert" className="form-input col-xs-12 col-sm-6 col-no-gutter">
                                    <input
                                        aria-required={isPrimaryAccount ? "true" : "false"}
                                        type="text"
                                        className={
                                            formData.emails.findIndex((element: any) => element.email === item.email) >= 0 &&
                                            visibleEmail.findIndex((element: any) => element.email === item.email) < 0 &&
                                            item.email !== ""
                                                ? "inputbox hide-input"
                                                : "inputbox"
                                        }
                                        onBlur={() => trigger(`emails[${index}].email`)}
                                        id={`label-email-${index + 1}`}
                                        placeholder={tLang("identityVault.monitoredInformation.personalInfoWidget.label.email")}
                                        name={`emails[${index}].email`}
                                        defaultValue={`${item!.email}`}
                                        maxLength={PATTERNS.EMAIL.maxLength}
                                        ref={register({
                                            validate: (): boolean | string => {
                                                const emails = getValues().emails;
                                                const duplicate = emails.reduce((p: any, c: any) => {
                                                    if (c.email === emails[index].email) p++;
                                                    return p;
                                                }, 0);
                                                if (duplicate > 1) {
                                                    return "Duplicate Emails";
                                                }
                                                return true;
                                            },
                                            required: isPrimaryAccount,
                                            pattern: PATTERNS.EMAIL.pattern,
                                        })}
                                    />
                                    <span
                                        hidden={
                                            formData.emails.findIndex((element: any) => element.email === item.email) < 0 ||
                                            item.email === "" ||
                                            visibleEmail.findIndex((element: any) => element.email === item.email) >= 0
                                        }
                                        style={{ marginTop: "10px " }}
                                    >
                                        <p style={{ wordWrap: "break-word", maxWidth: "70vw" }}>{emailMasking(item.email) + ' '.repeat(index)}</p>
                                    </span>
                                    <div className="form-input-button">
                                        {item.email !== "" && (
                                            <button
                                                type="button"
                                                className="btn-default btn-icon-only btn-no-padding"
                                                onClick={() => {
                                                    toggleEmailVisibility(item);
                                                }}
                                                title={"Show Email Text"}
                                            >
                                                <span className="btn-icon">
                                                    <em
                                                        className={`las ${
                                                            visibleEmail.findIndex((element: any) => element.email === item.email) < 0 ? "la-eye" : "la-eye-slash"
                                                        }`}
                                                    ></em>
                                                </span>
                                                <span className="btn-text">Show / Hide Email Text</span>
                                            </button>
                                        )}

                                        {emailHandler.fields && emailHandler.fields.length > (isPrimaryAccount ? 1 : 0) && (
                                            <button
                                                type="button"
                                                className="btn-default btn-icon-only btn-no-padding"
                                                style={{
                                                    marginTop:
                                                        formData.emails.findIndex((element: any) => element.email === item.email) >= 0 && item.email ? -10 : 0,
                                                }}
                                                onClick={() => {
                                                    emailHandler.remove(index);
                                                    window.addToDataLayer({ event: "Remove Alternate Email" });
                                                    setTimeout(() => trigger());
                                                }}
                                                aria-label="Remove Alternate Email"
                                            >
                                                <span className="btn-icon">
                                                    <em className="las la-trash-alt"></em>
                                                </span>
                                                <span className="btn-text">Remove Alternate Email</span>
                                            </button>
                                        )}
                                    </div>
                                    {!!errors["emails"] && !!errors["emails"][index] && errors["emails"][index]!.email!.type === "required" && (
                                        <span className="form-error">
                                            {tLang("identityVault.monitoredInformation.personalInfoWidget.validationMessage.emailRequiredError")}
                                        </span>
                                    )}
                                    {!!errors["emails"] && !!errors["emails"][index] && errors["emails"][index]!.email!.type === "pattern" && (
                                        <span className="form-error">
                                            {tLang("identityVault.monitoredInformation.personalInfoWidget.validationMessage.emailPatternError")}
                                        </span>
                                    )}
                                    {!!errors["emails"] && !!errors["emails"][index] && errors["emails"][index]!.email!.type === "validate" && (
                                        <span className="form-error">
                                            {tLang("identityVault.monitoredInformation.personalInfoWidget.validationMessage.emailDuplicateError")}
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                {activeStatus.isEmailAddressMonitoringActive && emailHandler.fields && emailHandler.fields.length < 1 && (
                    <div className="form-row inline no-margin row" aria-labelledby="label-full-email" role="group">
                        <div className="group-label col-xs-12 col-sm-4 col-no-gutter" id="label-full-email">
                            <label htmlFor="label-full-email">
                                {tLang("identityVault.monitoredInformation.personalInfoWidget.label.email")}
                                {isPrimaryAccount && (
                                    <span className="required" title="Mandatory">
                                        {" "}
                                        *
                                    </span>
                                )}
                            </label>
                        </div>
                        <div className="group-rows col-xs-12 col-sm-6 col-no-gutter">
                            <div className="row ">
                                <button
                                    type="button"
                                    className="btn-default btn-link btn-no-padding"
                                    onClick={() => {
                                        window.addToDataLayer({ event: "Add Email Address" });
                                        emailHandler.append({
                                            email: "",
                                        });
                                    }}
                                >
                                    <span className="btn-icon">
                                        <em className="las la-plus"></em>
                                    </span>
                                    <span className="btn-text">{tLang("identityVault.monitoredInformation.personalInfoWidget.addEmailLinkText")}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {activeStatus.isEmailAddressMonitoringActive && emailHandler.fields.length > 0 && emailHandler.fields.length < 10 && (
                    <div className="form-row row">
                        <div className="col-xs-12 col-sm-8 col-no-gutter col-sm-offset-4">
                            <button
                                type="button"
                                className="btn-default btn-link btn-no-padding"
                                onClick={() => {
                                    window.addToDataLayer({ event: "Add Email Address" });
                                    emailHandler.append({
                                        email: "",
                                    });
                                }}
                            >
                                <span className="btn-icon">
                                    <em className="las la-plus"></em>
                                </span>
                                <span className="btn-text">{tLang("identityVault.monitoredInformation.personalInfoWidget.addEmailLinkText")}</span>
                            </button>
                        </div>
                    </div>
                )}
                <br />
                {activeStatus.isPhoneMonitoringActive &&
                    phoneHandler.fields.map((item, index) => {
                        return (
                            <div key={item.id} className="form-row inline no-margin row" aria-labelledby={`label-full-phone-${index + 1}`} role="group">
                                <div className="group-label col-xs-12 col-sm-4 col-no-gutter" id={`label-full-phone-${index + 1}`}>
                                    {index === 0 ? (
                                        <label htmlFor={`label-phone-number-${index + 1}`}>
                                            {tLang("identityVault.monitoredInformation.personalInfoWidget.label.phone")}
                                            {isPrimaryAccount && (
                                                <span className="required" title="Mandatory">
                                                    {" "}
                                                    *
                                                </span>
                                            )}
                                        </label>
                                    ) :
                                        <label htmlFor={`label-phone-number-${index + 1}`} className="hide-label" >
                                            {tLang("identityVault.monitoredInformation.personalInfoWidget.label.phone")}+{`${index + 1}`}
                                        </label>}
                                </div>

                                <div className="group-rows col-xs-12 col-sm-6 col-no-gutter">
                                    <div className="row ">
                                        <div className="col-xs-12 col-no-gutter">
                                            <div
                                                className={`form-row inline row ${errors["phone"] && errors["phone"][index] && "status-error"} ${
                                                    errors["phone"] &&
                                                    errors["phone"][index] &&
                                                    watch(`phone[${index}].phone`) &&
                                                    !parsePhoneNumber(watch(`phone[${index}].phone`))
                                                        ? "country-error"
                                                        : ""
                                                }`}
                                            >
                                                <div className="form-label sr-only col-xs-12  col-no-gutter">
                                                    <label htmlFor={`label-phone-number${index + 1}`}>Phone</label>
                                                </div>
                                                <div role="alert" className="form-input col-xs-12  col-no-gutter">
                                                    <Controller
                                                        aria-required={isPrimaryAccount ? "true" : "false"}
                                                        name={`phone[${index}].phone`}
                                                        control={control}
                                                        defaultValue={item.phone ? item.phone : ""}
                                                        className="inputbox"
                                                        rules={{
                                                            required: isPrimaryAccount,
                                                            validate: (value): boolean | string => {
                                                                const phone = getValues().phone;
                                                                const duplicate = phone.reduce((p: any, c: any) => {
                                                                    if (c.phone === phone[index].phone) p++;
                                                                    return p;
                                                                }, 0);
                                                                if (duplicate > 1) {
                                                                    return getDuplicateValidationError();
                                                                } else {
                                                                    return isPrimaryAccount
                                                                        ? isValidPhoneNo(accountInfo && accountInfo.country ? accountInfo.country : "US", value)
                                                                        : value
                                                                        ? isValidPhoneNo(accountInfo && accountInfo.country ? accountInfo.country : "US", value)
                                                                        : true;
                                                                }
                                                            },
                                                        }}
                                                        render={({ onChange, value }) => (
                                                            <PhoneInput
                                                                aria-required={isPrimaryAccount ? "true" : "false"}
                                                                value={value}
                                                                onChange={onChange}
                                                                international={false}
                                                                addInternationalOption={false}
                                                                defaultCountry={accountInfo && accountInfo.country ? accountInfo.country : "US"}
                                                                countries={[accountInfo && accountInfo.country ? accountInfo.country : "US"]}
                                                                id={`label-phone-number-${index + 1}`}
                                                                placeholder={tLang("identityVault.monitoredInformation.personalInfoWidget.placeholder.phone")}
                                                                maxLength={15}
                                                                minLength={10}
                                                            />
                                                        )}
                                                    />
                                                    {!!errors["phone"] && !!errors["phone"][index] && errors["phone"][index]!.phone!.type === "required" && (
                                                        <span className="form-error">
                                                            {tLang("identityVault.monitoredInformation.personalInfoWidget.validationMessage.phoneRequiredError")}
                                                        </span>
                                                    )}
                                                    {!!errors["phone"] && !!errors["phone"][index] && errors["phone"][index]!.phone!.type === "validate" && (
                                                        <span className="form-error">
                                                            {errors["phone"][index]!.phone!.message !== ""
                                                                ? errors["phone"][index]!.phone!.message
                                                                : tLang("identityVault.monitoredInformation.personalInfoWidget.validationMessage.phonePatternError1") }
                                                        </span>
                                                    )}
                                                    {!!errors["phone"] &&
                                                        !!errors["phone"][index] &&
                                                        errors["phone"][index]!.phone!.type === "minLength" &&
                                                        (getCountryInfo() ? (
                                                            <span className="form-error">
                                                                {tLang(
                                                                    "identityVault.monitoredInformation.personalInfoWidget.validationMessage.phoneMinlengthError"
                                                                )}
                                                            </span>
                                                        ) : (
                                                            <span className="form-error">
                                                                {tLang("identityVault.monitoredInformation.personalInfoWidget.validationMessage.phonePatternError")}
                                                            </span>
                                                        ))}
                                                    {!!errors["phone"] &&
                                                        !!errors["phone"][index] &&
                                                        errors["phone"][index]!.phone!.type === "maxLength" &&
                                                        (getCountryInfo() ? (
                                                            <span className="form-error">
                                                                {tLang(
                                                                    "identityVault.monitoredInformation.personalInfoWidget.validationMessage.phoneMaxlengthError"
                                                                )}
                                                            </span>
                                                        ) : (
                                                            <span className="form-error">
                                                                {tLang("identityVault.monitoredInformation.personalInfoWidget.validationMessage.phonePatternError")}
                                                            </span>
                                                        ))}

                                                    <div className="form-input-button">
                                                        {phoneHandler.fields && phoneHandler.fields.length > (isPrimaryAccount ? 1 : 0) && (
                                                            <button
                                                                type="button"
                                                                className="btn-default btn-icon-only btn-no-padding"
                                                                onClick={() => {
                                                                    phoneHandler.remove(index);
                                                                    window.addToDataLayer({ event: "Remove Alternate Phone" });
                                                                }}
                                                                aria-label="Remove Alternate Phone"
                                                            >
                                                                <span className="btn-icon">
                                                                    <em className="las la-trash-alt"></em>
                                                                </span>
                                                                <span className="btn-text">Remove Alternate Phone</span>
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                {activeStatus.isPhoneMonitoringActive && phoneHandler.fields && phoneHandler.fields.length < 1 && (
                    <div className="form-row inline no-margin row" aria-labelledby="label-full-phone" role="group">
                        <div className="group-label col-xs-12 col-sm-4 col-no-gutter" id="label-full-phone">
                            <label htmlFor="label-phone-number">
                                {tLang("identityVault.monitoredInformation.personalInfoWidget.label.phone")}
                                {isPrimaryAccount && (
                                    <span className="required" title="Mandatory">
                                        {" "}
                                        *
                                    </span>
                                )}
                            </label>
                        </div>
                        <div className="group-rows col-xs-12 col-sm-6 col-no-gutter">
                            <div className="row ">
                                <button
                                    type="button"
                                    className="btn-default btn-link btn-no-padding"
                                    onClick={() => {
                                        phoneHandler.append({ code: +1, phone: "" });
                                        window.addToDataLayer({ event: "Add Phone Number" });
                                        disableFlag();
                                    }}
                                >
                                    <span className="btn-icon">
                                        <em className="las la-plus"></em>
                                    </span>
                                    <span className="btn-text">{tLang("identityVault.monitoredInformation.personalInfoWidget.addPhoneLinkText")}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {activeStatus.isPhoneMonitoringActive && phoneHandler.fields.length > 0 && phoneHandler.fields.length < (isLife360PartnerId ? 1 : 10) && (
                    <div className="form-row row">
                        <div className="col-xs-12 col-sm-8 col-no-gutter col-sm-offset-4">
                            {activeStatus.isDarkWebMonitoringActive && (
                                <button
                                    type="button"
                                    className="btn-default btn-link btn-no-padding"
                                    onClick={() => {
                                        window.addToDataLayer({ event: "Add Phone Number" });
                                        phoneHandler.append({ code: +1, phone: "" });
                                        disableFlag();
                                    }}
                                >
                                    <span className="btn-icon">
                                        <em className="las la-plus"></em>
                                    </span>
                                    <span className="btn-text">{tLang("identityVault.monitoredInformation.personalInfoWidget.addPhoneLinkText")}</span>
                                </button>
                            )}
                        </div>
                    </div>
                )}
                <br />
                {!isLife360PartnerId && (activeStatus.isDOBAndSSNDisplayable || activeStatus.isDarkWebMonitoringActive) && (
                    <fieldset className="form-row inline">
                        <div className="row">
                            <div className="col-xs-12 col-sm-4 col-no-gutter">
                                <legend className="legend-label">
                                    <label htmlFor="label-dob">
                                        {tLang("identityVault.monitoredInformation.personalInfoWidget.label.dob")}{" "}
                                        <span className="required" title="Mandatory">
                                            *
                                        </span>
                                    </label>
                                </legend>
                            </div>

                            <div className="col-xs-12 col-sm-6 col-no-gutter">
                                <div className="row ">
                                    {formData["address-country"] === "US" ? (
                                        <>
                                            {dateFields[1]} {dateFields[0]} {dateFields[2]}{" "}
                                        </>
                                    ) : formData["address-country"] === "CA" || formData["address-country"] === "TW" ? (
                                        <>
                                            {dateFields[2]} {dateFields[1]} {dateFields[0]}{" "}
                                        </>
                                    ) : (
                                        <>
                                            {dateFields[0]} {dateFields[1]} {dateFields[2]}
                                        </>
                                    )}
                                    {errors["birthYear"] && errors["birthYear"].type === "validate" && (
                                        <span className="form-error">{tLang("identityVault.monitoredInformation.personalInfoWidget.validationMessage.dob")}</span>
                                    )}
                                    {((errors["birthYear"] && errors["birthYear"].type === "required") ||
                                        (errors["birthDay"] && errors["birthDay"].type === "required") ||
                                        (errors["birthMonth"] && errors["birthMonth"].type === "required")) && (
                                        <span className="form-error">
                                            {tLang("identityVault.monitoredInformation.personalInfoWidget.validationMessage.dobRequiredError")}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </fieldset>
                )}

                {!isLife360PartnerId && (activeStatus.isDOBAndSSNDisplayable || activeStatus.isDarkWebMonitoringActive) && (
                    <>
                        {(formData["address-country"] === "US" || formData["address-country"] === "United States of America" || !isPrimaryAccount || formData["address-country"] == "" || Boolean(formData.securityNumber)) && (
                            <div
                                className={`form-row inline row ${
                                    (errors["check-ssn-3-digit"] || errors["check-ssn-2-digit"] || errors["check-ssn-4-digit"]) && "status-error"
                                }`}
                            >
                                <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                                    <label htmlFor="check-ssn-3-digit">
                                        {tLang("identityVault.monitoredInformation.personalInfoWidget.label.ssn")}{" "}
                                        <span className="required" title="Mandatory">
                                            *
                                        </span>
                                    </label>
                                </div>
                                <div className="form-input col-xs-12 col-sm-6 col-no-gutter">
                                    {!formData.securityNumber ? (
                                        <div className="form-input row">
                                            <div role="alert" className="col-xs-4 col-sm-4 col-no-gutter">
                                                <input
                                                    aria-required="true"
                                                    ref={register({ required: true, pattern: /(^(?!0{3})(\d{3})(?!-?0{3})(|-\d{3})?$)/ })}
                                                    type={"text"}
                                                    id="check-ssn-3-digit"
                                                    name="check-ssn-3-digit"
                                                    maxLength={3}
                                                    className="inputbox"
                                                    autoComplete="off"
                                                    onChange={onTextBoxChangeHandler}
                                                    aria-label="Social Security Number"
                                                />
                                            </div>
                                            <div role="alert" className="col-xs-4 col-sm-3 col-gutter-horizontal">
                                                <input
                                                    aria-required="true"
                                                    ref={register({ required: true, pattern: /(^(\d{2})(?!-?0{2})(|-\d{2})?$)/ })}
                                                    type={"text"}
                                                    name="check-ssn-2-digit"
                                                    id="check-ssn-2-digit"
                                                    maxLength={2}
                                                    autoComplete="off"
                                                    className="inputbox"
                                                    onChange={onTextBoxChangeHandler}
                                                    aria-label="Social Security Number "
                                                />
                                            </div>
                                            <div role="alert" className="col-xs-4 col-sm-5 col-no-gutter">
                                                <input
                                                    aria-required="true"
                                                    ref={register({ required: true, pattern: /(^(?!0{4})(\d{4})(?!-?0{4})(|-\d{4})?$)/ })}
                                                    type={"text"}
                                                    name="check-ssn-4-digit"
                                                    id="check-ssn-4-digit"
                                                    maxLength={4}
                                                    autoComplete="off"
                                                    className="inputbox"
                                                    onChange={onTextBoxChangeHandler}
                                                    aria-label=" Social Security Number "
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <div>
                                            <label htmlFor="label-ssn" className="hide-label">
                                                {tLang("identityVault.monitoredInformation.personalInfoWidget.label.ssn")}{" "}
                                            </label>
                                            <input
                                                ref={register}
                                                readOnly={true}
                                                name="securityNumber"
                                                className="inputbox"
                                                id="label-ssn"
                                                maxLength={11}
                                            />
                                        </div>
                                    )}
                                    {({ ...errors["check-ssn-3-digit"] }.type === "required" ||
                                        { ...errors["check-ssn-2-digit"] }.type === "required" ||
                                        { ...errors["check-ssn-4-digit"] }.type === "required") && (
                                        <span className="form-error">
                                            {tLang("identityVault.monitoredInformation.personalInfoWidget.validationMessage.ssnRequiredError")}
                                        </span>
                                    )}

                                    {({ ...errors["check-ssn-3-digit"] }.type === "pattern" ||
                                        { ...errors["check-ssn-2-digit"] }.type === "pattern" ||
                                        { ...errors["check-ssn-4-digit"] }.type === "pattern") && (
                                        <span className="form-error">
                                            {tLang("identityVault.monitoredInformation.personalInfoWidget.validationMessage.ssnPatternError")}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}

                        {(formData["address-country"] === "CA" || formData["address-country"] === "Canada" || Boolean(formData.internationalSecurityNumber["sin"])) && (
                            <div className={`form-row inline row ${errors["internationalSecurityNumber"] && "status-error"}`}>
                                <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                                    <label htmlFor="label-sin">{tLang("identityVault.monitoredInformation.personalInfoWidget.label.sin")}</label>
                                </div>
                                <div role="alert" className="form-input col-xs-12 col-sm-6 col-no-gutter">
                                    {!Boolean(formData.internationalSecurityNumber["sin"]) ? (
                                        <input
                                            aria-required="true"
                                            className="inputbox"
                                            id="label-sin"
                                            ref={register({ maxLength: 9, minLength: 9, pattern: PATTERNS.SIN.pattern })}
                                            name="internationalSecurityNumber"
                                            maxLength={9}
                                        />
                                    ) : (
                                        <input
                                            aria-required="true"
                                            className="inputbox"
                                            id="label-sin"
                                            ref={register()}
                                            readOnly={true}
                                            name="internationalSecurityNumber"
                                        />
                                    )}

                                    {errors["internationalSecurityNumber"] && errors["internationalSecurityNumber"].type === "required" && (
                                        <span className="form-error">
                                            {tLang("identityVault.monitoredInformation.personalInfoWidget.validationMessage.sinRequiredError")}
                                        </span>
                                    )}
                                    {errors["internationalSecurityNumber"] && errors["internationalSecurityNumber"].type === "pattern" && (
                                        <span className="form-error">
                                            {tLang("identityVault.monitoredInformation.personalInfoWidget.validationMessage.sinPatternError")}
                                        </span>
                                    )}
                                    {errors["internationalSecurityNumber"] && errors["internationalSecurityNumber"].type === "minLength" && (
                                        <span className="form-error">
                                            {tLang("identityVault.monitoredInformation.personalInfoWidget.validationMessage.sinMinlengthError")}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}

                        {((formData["address-country"] !== "CA" && formData["address-country"] !== "Canada" && formData["address-country"] !== "US" && formData["address-country"] !== "United States of America" && isPrimaryAccount) ||
                            Boolean(formData.nationalIdentificationNumber["nid"])) && (
                            <div className={`form-row inline row ${errors["nationalIdentificationNumber"] && "status-error"}`}>
                                <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                                    <label htmlFor="label-nid">{tLang("identityVault.monitoredInformation.personalInfoWidget.label.nid")}</label>
                                </div>
                                <div className="form-input col-xs-12 col-sm-6 col-no-gutter">
                                    {!Boolean(formData.nationalIdentificationNumber["nid"]) ? (
                                        <input
                                            className="inputbox"
                                            id="label-nid"
                                            ref={register({ maxLength: 19, minLength: 8, pattern: PATTERNS.NID.pattern })}
                                            name="nationalIdentificationNumber"
                                            maxLength={19}
                                        />
                                    ) : (
                                        <input
                                            className="inputbox"
                                            id="label-nid"
                                            ref={register()}
                                            readOnly={true}
                                            name="nationalIdentificationNumber"
                                        />
                                    )}

                                    {errors["nationalIdentificationNumber"] && errors["nationalIdentificationNumber"].type === "required" && (
                                        <span className="form-error">
                                            {tLang("identityVault.monitoredInformation.personalInfoWidget.validationMessage.nidRequiredError")}
                                        </span>
                                    )}
                                    {errors["nationalIdentificationNumber"] && errors["nationalIdentificationNumber"].type === "pattern" && (
                                        <span className="form-error">
                                            {tLang("identityVault.monitoredInformation.personalInfoWidget.validationMessage.nidPatternError")}
                                        </span>
                                    )}
                                    {errors["nationalIdentificationNumber"] && errors["nationalIdentificationNumber"].type === "minLength" && (
                                        <span className="form-error">
                                            {tLang("identityVault.monitoredInformation.personalInfoWidget.validationMessage.nidMinlengthError")}
                                        </span>
                                    )}
                                    {errors["nationalIdentificationNumber"] && errors["nationalIdentificationNumber"].type === "maxLength" && (
                                        <span className="form-error">
                                            {tLang("identityVault.monitoredInformation.personalInfoWidget.validationMessage.nidMaxlengthError")}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}
                <div className="margin-vertical-l"></div>

                <div className="row between-xs">
                    <div className="col-xs col-no-gutter flex-grow-null"></div>
                    <div className="col-xs col-no-gutter flex-grow-null">
                        <ul className="list-inline list-reset">
                            <li>
                                <button
                                    type="button"
                                    className="btn-default btn-link btn-no-padding btn-no-border btn-min-width"
                                    onClick={() => {
                                        setStatus(false);
                                        window.addToDataLayer({ event: "Cancel Editing Personal Information" });
                                    }}
                                >
                                    <span className="btn-text">{tLang("identityVault.monitoredInformation.personalInfoWidget.cancelFormButtonText")}</span>
                                </button>
                            </li>
                            <li className="margin-left-s">
                                <button
                                    type="submit"
                                    className="btn-default btn-primary btn-min-width"
                                    onClick={() => window.addToDataLayer({ event: "Save Edited Personal Information" })}
                                >
                                    <span className="btn-text">{tLang("identityVault.monitoredInformation.personalInfoWidget.saveButtonText")}</span>
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default PersonalInfoForm;
